import React, { useState, useEffect } from 'react';
import { LifeBuoy, MessageSquare, CheckCircle, Clock, AlertCircle, ExternalLink, RefreshCw, Loader2, Filter, Search, Bug } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { projectId } from '../utils/supabase/info';
import { TicketDetailModal } from './TicketDetailModal';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface SupportTicket {
  id: string;
  firmId: string;
  firmName: string;
  requestType: 'billing' | 'technical' | 'feature' | 'bug' | 'account';
  summary: string;
  description: string;
  status: string;
  priority: string;
  impact?: string;
  urgency?: string;
  createdBy: string;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  jiraUrl: string;
  lastResponse?: string;
  lastResponseAt?: string;
  lastResponseBy?: string;
}

interface SystemAdminSupportQueueProps {
  accessToken: string;
}

export function SystemAdminSupportQueue({ accessToken }: SystemAdminSupportQueueProps) {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);

  useEffect(() => {
    fetchAllTickets();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tickets, statusFilter, priorityFilter, requestTypeFilter, searchQuery]);

  const fetchAllTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/admin/tickets`,
        {
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }

      const data = await response.json();
      console.log('[SUPPORT QUEUE] Fetched tickets:', data.tickets.length);
      setTickets(data.tickets || []);
    } catch (error) {
      console.error('[SUPPORT QUEUE] Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const runDiagnostic = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/admin/diagnostic`,
        {
          headers: {
            ...bearerAuthHeader(),
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to run diagnostic');
      }

      const data = await response.json();
      console.log('[SUPPORT QUEUE] Diagnostic data:', data);
      setDiagnosticData(data);
      setShowDiagnostic(true);
    } catch (error) {
      console.error('[SUPPORT QUEUE] Error running diagnostic:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...tickets];

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'open') {
        filtered = filtered.filter(t => ['Open', 'In Progress', 'Waiting for Customer'].includes(t.status));
      } else if (statusFilter === 'resolved') {
        filtered = filtered.filter(t => ['Resolved', 'Closed'].includes(t.status));
      } else {
        filtered = filtered.filter(t => t.status === statusFilter);
      }
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(t => t.priority === priorityFilter);
    }

    // Request type filter
    if (requestTypeFilter !== 'all') {
      filtered = filtered.filter(t => t.requestType === requestTypeFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.summary.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.firmName.toLowerCase().includes(query) ||
        t.createdByEmail.toLowerCase().includes(query) ||
        t.id.toLowerCase().includes(query)
      );
    }

    setFilteredTickets(filtered);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', icon: React.ReactNode }> = {
      'Open': { variant: 'destructive', icon: <AlertCircle className="h-3 w-3" /> },
      'In Progress': { variant: 'default', icon: <Clock className="h-3 w-3" /> },
      'Waiting for Customer': { variant: 'secondary', icon: <MessageSquare className="h-3 w-3" /> },
      'Resolved': { variant: 'outline', icon: <CheckCircle className="h-3 w-3" /> },
      'Closed': { variant: 'outline', icon: <CheckCircle className="h-3 w-3" /> },
    };

    const config = statusMap[status] || { variant: 'secondary' as const, icon: null };
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {config.icon}
        {status}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityColors: Record<string, string> = {
      'Highest': 'bg-red-100 text-red-800 border-red-300',
      'High': 'bg-orange-100 text-orange-800 border-orange-300',
      'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Low': 'bg-blue-100 text-blue-800 border-blue-300',
      'Lowest': 'bg-gray-100 text-gray-800 border-gray-300',
    };

    return (
      <Badge variant="outline" className={priorityColors[priority] || ''}>
        {priority}
      </Badge>
    );
  };

  const getRequestTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      billing: 'BIL',
      technical: 'TEC',
      feature: 'FEA',
      bug: 'BUG',
      account: 'ACC',
    };
    return icons[type] || 'OTH';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const openTicketCount = tickets.filter(t => ['Open', 'In Progress', 'Waiting for Customer'].includes(t.status)).length;
  const resolvedTicketCount = tickets.filter(t => ['Resolved', 'Closed'].includes(t.status)).length;
  
  return (
    <>
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdate={() => {
            fetchAllTickets();
          }}
          companyId={selectedTicket.firmId}
          accessToken={accessToken}
        />
      )}
      
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5" />
              Support Ticket Queue
            </CardTitle>
            <CardDescription>
              All support tickets across all consulting firms
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runDiagnostic}
            >
              <Bug className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAllTickets}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4">
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Total Tickets</span>
            <span className="text-2xl font-semibold">{tickets.length}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Open / Active</span>
            <span className="text-2xl font-semibold text-orange-600">{openTicketCount}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">Resolved</span>
            <span className="text-2xl font-semibold text-green-600">{resolvedTicketCount}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open/Active</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="In Progress">In Progress</SelectItem>
              <SelectItem value="Waiting for Customer">Waiting for Customer</SelectItem>
              <SelectItem value="resolved">Resolved/Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="Highest">Highest</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Lowest">Lowest</SelectItem>
            </SelectContent>
          </Select>
          <Select value={requestTypeFilter} onValueChange={setRequestTypeFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="billing">Billing</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
              <SelectItem value="feature">Feature</SelectItem>
              <SelectItem value="bug">Bug</SelectItem>
              <SelectItem value="account">Account</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {showDiagnostic && diagnosticData && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Bug className="h-4 w-4" />
                KV Store Diagnostic
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDiagnostic(false)}
              >
                Close
              </Button>
            </div>
            <div className="space-y-2 text-sm font-mono">
              <div>
                <strong>Index Keys Found:</strong> {diagnosticData.indexKeysCount}
              </div>
              {diagnosticData.indexKeys && diagnosticData.indexKeys.length > 0 && (
                <div className="pl-4">
                  {diagnosticData.indexKeys.map((idx: any, i: number) => (
                    <div key={i}>
                      • {idx.key}: {idx.valueLength} tickets {JSON.stringify(idx.value)}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <strong>Ticket Keys Found:</strong> {diagnosticData.ticketKeysCount}
              </div>
              {diagnosticData.ticketKeys && diagnosticData.ticketKeys.length > 0 && (
                <div className="pl-4 max-h-40 overflow-y-auto">
                  {diagnosticData.ticketKeys.map((tk: any, i: number) => (
                    <div key={i}>
                      • {tk.key} {tk.hasData ? '✓' : '✗'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <LifeBuoy className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No tickets found</p>
            {searchQuery && <p className="text-sm mt-1">Try adjusting your search or filters</p>}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Submitter</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.map((ticket) => (
                <TableRow 
                  key={ticket.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    // If it's a KV-only ticket, open the modal; otherwise open Jira
                    if (ticket.id.startsWith('KV-') || !ticket.jiraUrl) {
                      setSelectedTicket(ticket);
                    } else {
                      window.open(ticket.jiraUrl, '_blank');
                    }
                  }}
                >
                  <TableCell className="font-mono text-sm">
                    <div className="flex items-center gap-2">
                      <span>{getRequestTypeIcon(ticket.requestType)}</span>
                      <span>{ticket.id}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[150px]">
                      <p className="truncate font-medium">{ticket.firmName}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[300px]">
                      <p className="truncate font-medium">{ticket.summary}</p>
                      {ticket.description && (
                        <p className="truncate text-sm text-muted-foreground">{ticket.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(ticket.status)}
                  </TableCell>
                  <TableCell>
                    {getPriorityBadge(ticket.priority)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(ticket.createdAt)}
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[150px]">
                      <p className="truncate text-sm">{ticket.createdByName}</p>
                      <p className="truncate text-xs text-muted-foreground">{ticket.createdByEmail}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click when clicking button directly
                        // If it's a KV-only ticket, open the modal; otherwise open Jira
                        if (ticket.id.startsWith('KV-') || !ticket.jiraUrl) {
                          setSelectedTicket(ticket);
                        } else {
                          window.open(ticket.jiraUrl, '_blank');
                        }
                      }}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    </>
  );
}